/**
 * Builder + Solution: the typed wrapper over the xsec_geom.h handle ABI.
 * Author a cross-section (typed calls or a parsed `.xsec` text), solve, then
 * query aggregates, field grids, geometry outlines, meshes, and modes.
 */
import { check, copyF64, copyI32, copyU8, readF64, readI32, readPtr, withScratch, } from "./module.js";
import { DIFF, DOMAIN, FIELD_GEOM, FIELD_GRID, FIELD_MESH, MODE, POLYLINE, SOLVE_LEVEL, SURFACE_CHARGE } from "./layouts.js";
/** Boundary condition kinds (mirror of xsec_bc_kind). */
export var BoundaryKind;
(function (BoundaryKind) {
    BoundaryKind[BoundaryKind["Conductor"] = 0] = "Conductor";
    BoundaryKind[BoundaryKind["Interface"] = 1] = "Interface";
})(BoundaryKind || (BoundaryKind = {}));
/** Solver backing (mirror of xsec_solver_kind). */
export var SolverKind;
(function (SolverKind) {
    SolverKind[SolverKind["Bem"] = 0] = "Bem";
    SolverKind[SolverKind["Fem"] = 1] = "Fem";
    SolverKind[SolverKind["Fdm"] = 2] = "Fdm";
    SolverKind[SolverKind["Eem"] = 3] = "Eem";
})(SolverKind || (SolverKind = {}));
/** Analysis kind (mirror of xsec_analysis_kind). */
export var AnalysisKind;
(function (AnalysisKind) {
    AnalysisKind[AnalysisKind["QuasiTem"] = 0] = "QuasiTem";
    AnalysisKind[AnalysisKind["Diff"] = 1] = "Diff";
    AnalysisKind[AnalysisKind["Te"] = 2] = "Te";
    AnalysisKind[AnalysisKind["Tm"] = 3] = "Tm";
    AnalysisKind[AnalysisKind["Hybrid"] = 4] = "Hybrid";
})(AnalysisKind || (AnalysisKind = {}));
/**
 * Fluent builder for an arbitrary multi-region cross-section. Author
 * boundaries so region_a is on the right of travel (see xsec_geom.h).
 * Always {@link dispose}.
 */
export class Builder {
    m;
    h;
    disposed = false;
    /** @internal — use Xsec.builder(). */
    constructor(m) {
        this.m = m;
        this.h = m.ccall("xsec_builder_create", "number", [], []);
        if (!this.h)
            throw new Error("xsec_builder_create failed (out of memory)");
    }
    /** @internal */
    get module() {
        return this.m;
    }
    /** Add a dielectric region; returns its region id. */
    addRegion(epsilonR) {
        return check(this.m, this.m.ccall("xsec_builder_add_region", "number", ["number", "number"], [this.h, epsilonR]));
    }
    /** Start a boundary; returns its boundary id. regionB = -1 for a conductor. */
    addBoundary(kind, voltage, regionA, regionB) {
        return check(this.m, this.m.ccall("xsec_builder_add_boundary", "number", ["number", "number", "number", "number", "number"], [this.h, kind, voltage, regionA, regionB]));
    }
    addSegment(boundaryId, x0, y0, x1, y1, nSegments = 0) {
        check(this.m, this.m.ccall("xsec_builder_add_segment", "number", ["number", "number", "number", "number", "number", "number", "number"], [this.h, boundaryId, x0, y0, x1, y1, nSegments]));
        return this;
    }
    addArc(boundaryId, cx, cy, r, theta0, theta1, nSegments = 0) {
        check(this.m, this.m.ccall("xsec_builder_add_arc", "number", ["number", "number", "number", "number", "number", "number", "number", "number"], [this.h, boundaryId, cx, cy, r, theta0, theta1, nSegments]));
        return this;
    }
    /**
     * Parse a complete `.xsec` text description into this builder, REPLACING any
     * geometry authored so far. Throws with the parser's line-numbered reason on
     * a malformed text.
     */
    parse(xsecText) {
        check(this.m, this.m.ccall("xsec_builder_parse", "number", ["number", "string"], [this.h, xsecText]));
        return this;
    }
    setSolver(kind) {
        check(this.m, this.m.ccall("xsec_builder_set_solver", "number", ["number", "number"], [this.h, kind]));
        return this;
    }
    setAnalysis(kind) {
        check(this.m, this.m.ccall("xsec_builder_set_analysis", "number", ["number", "number"], [this.h, kind]));
        return this;
    }
    setFrequency(hz) {
        check(this.m, this.m.ccall("xsec_builder_set_frequency", "number", ["number", "number"], [this.h, hz]));
        return this;
    }
    setNModes(n) {
        check(this.m, this.m.ccall("xsec_builder_set_n_modes", "number", ["number", "number"], [this.h, n]));
        return this;
    }
    /** keys: bem_order, fem_dof, fdm_target, refine_steps, refine_target_pm,
     *  refine_max_dof, refine_theta_pm, keep_level_meshes, field. */
    setOptionInt(key, value) {
        check(this.m, this.m.ccall("xsec_builder_set_option_int", "number", ["number", "string", "number"], [this.h, key, value]));
        return this;
    }
    /** Run semantic checks; returns tri-state validity (-1 invalid, 0 unchecked, 1 valid). */
    validate() {
        return check(this.m, this.m.ccall("xsec_builder_validate", "number", ["number"], [this.h]));
    }
    /**
     * Auto-validates and solves. Returns a {@link Solution} the caller must
     * dispose. The builder stays usable (rebuild/re-solve).
     */
    solve() {
        const sol = withScratch(this.m, 4, (outPtr) => {
            check(this.m, this.m.ccall("xsec_builder_solve", "number", ["number", "number"], [this.h, outPtr]));
            return readPtr(this.m, outPtr);
        });
        return new Solution(this.m, sol);
    }
    /** Free the underlying builder handle (idempotent). */
    dispose() {
        if (!this.disposed) {
            this.m.ccall("xsec_builder_destroy", "number", ["number"], [this.h]);
            this.disposed = true;
        }
    }
}
/** A solved cross-section. Scalars are cached; field queries hit the WASM heap. */
export class Solution {
    m;
    s;
    z0;
    capacitance;
    dof;
    errorEstimate;
    converged;
    disposed = false;
    /** @internal — obtained from Builder.solve(). */
    constructor(m, s) {
        this.m = m;
        this.s = s;
        this.z0 = this.scalarF64("xsec_solution_z0");
        this.capacitance = this.scalarF64("xsec_solution_capacitance");
        this.errorEstimate = this.scalarF64("xsec_solution_error_estimate");
        this.dof = withScratch(m, 4, (p) => {
            check(m, m.ccall("xsec_solution_dof", "number", ["number", "number"], [s, p]));
            return readI32(m, p);
        });
        this.converged = withScratch(m, 4, (p) => {
            check(m, m.ccall("xsec_solution_converged", "number", ["number", "number"], [s, p]));
            return readI32(m, p) !== 0;
        });
    }
    scalarF64(fn) {
        return withScratch(this.m, 8, (p) => {
            check(this.m, this.m.ccall(fn, "number", ["number", "number"], [this.s, p]));
            return readF64(this.m, p);
        });
    }
    /**
     * Effective permittivity C/C_air from the quasi-TEM dual-energy solve.
     * Falls back to capacitance/`fallbackC0` (e.g. the closed-form vacuum
     * capacitance) when the ABI reports none; throws if neither is available.
     */
    epsEff(fallbackC0) {
        const st = withScratch(this.m, 8, (p) => {
            const r = this.m.ccall("xsec_solution_eps_eff", "number", ["number", "number"], [this.s, p]);
            return r >= 0 ? readF64(this.m, p) : NaN;
        });
        if (Number.isFinite(st))
            return st;
        if (fallbackC0 && fallbackC0 > 0)
            return this.capacitance / fallbackC0;
        throw new Error("xsec: no effective permittivity on this solution");
    }
    /**
     * Sample V (and optionally E/H) on an nx*ny grid over the given box.
     * fields: "V" (default) | "E" (V+E) | "H" (V+E+H).
     */
    sampleGrid(x0, y0, x1, y1, nx, ny, fields = "E", mode = 0) {
        const flags = fields === "V" ? 0 : fields === "E" ? 1 : 3;
        const g = withScratch(this.m, 4, (outPtr) => {
            check(this.m, this.m.ccall("xsec_solution_sample_grid_mode", "number", ["number", "number", "number", "number", "number", "number", "number", "number", "number", "number"], [this.s, mode, x0, y0, x1, y1, nx, ny, flags, outPtr]));
            return readPtr(this.m, outPtr);
        });
        try {
            const m = this.m;
            const gnx = readI32(m, g + FIELD_GRID.nx);
            const gny = readI32(m, g + FIELD_GRID.ny);
            const n = gnx * gny;
            const arr = (off) => {
                const p = readPtr(m, g + off);
                return p ? copyF64(m, p, n) : null;
            };
            return {
                nx: gnx,
                ny: gny,
                x0: readF64(m, g + FIELD_GRID.x0),
                y0: readF64(m, g + FIELD_GRID.y0),
                x1: readF64(m, g + FIELD_GRID.x1),
                y1: readF64(m, g + FIELD_GRID.y1),
                V: arr(FIELD_GRID.V),
                Ex: arr(FIELD_GRID.Ex),
                Ey: arr(FIELD_GRID.Ey),
                Hx: arr(FIELD_GRID.Hx),
                Hy: arr(FIELD_GRID.Hy),
                mask: copyU8(m, readPtr(m, g + FIELD_GRID.mask), n),
            };
        }
        finally {
            this.m.ccall("xsec_field_grid_destroy", null, ["number"], [g]);
        }
    }
    /** Number of sampleable field modes (1, or 2 for DIFF, N for waveport). */
    fieldModes() {
        return withScratch(this.m, 4, (p) => {
            check(this.m, this.m.ccall("xsec_solution_field_modes", "number", ["number", "number"], [this.s, p]));
            return readI32(this.m, p);
        });
    }
    /** Conductor/ground/interface outlines with per-region permittivities. */
    geometry() {
        const g = withScratch(this.m, 4, (outPtr) => {
            check(this.m, this.m.ccall("xsec_solution_geometry", "number", ["number", "number"], [this.s, outPtr]));
            return readPtr(this.m, outPtr);
        });
        try {
            const m = this.m;
            const nPoly = readI32(m, g + FIELD_GEOM.nPolylines);
            const polyBase = readPtr(m, g + FIELD_GEOM.polylines);
            const polylines = [];
            for (let i = 0; i < nPoly; i++) {
                const p = polyBase + i * POLYLINE.size;
                const nPts = readI32(m, p + POLYLINE.nPoints);
                const closed = readI32(m, p + POLYLINE.closed) !== 0;
                const curvePtr = readPtr(m, p + POLYLINE.edgeCurve);
                const nEdges = closed ? nPts : nPts - 1;
                polylines.push({
                    marker: readI32(m, p + POLYLINE.marker),
                    closed,
                    xy: copyF64(m, readPtr(m, p + POLYLINE.xy), 2 * nPts),
                    epsA: readF64(m, p + POLYLINE.epsA),
                    epsB: readF64(m, p + POLYLINE.epsB),
                    edgeCurve: curvePtr ? copyF64(m, curvePtr, 3 * nEdges) : null,
                });
            }
            const nReg = readI32(m, g + FIELD_GEOM.nRegions);
            return {
                polylines,
                regionEpsilon: copyF64(m, readPtr(m, g + FIELD_GEOM.regionEpsilon), nReg),
                x0: readF64(m, g + FIELD_GEOM.x0),
                y0: readF64(m, g + FIELD_GEOM.y0),
                x1: readF64(m, g + FIELD_GEOM.x1),
                y1: readF64(m, g + FIELD_GEOM.y1),
            };
        }
        finally {
            this.m.ccall("xsec_field_geom_destroy", null, ["number"], [g]);
        }
    }
    /** The valid field region (bounded flag + bbox). */
    domain() {
        return withScratch(this.m, DOMAIN.size, (p) => {
            check(this.m, this.m.ccall("xsec_solution_domain", "number", ["number", "number"], [this.s, p]));
            return {
                bounded: readI32(this.m, p + DOMAIN.bounded) !== 0,
                x0: readF64(this.m, p + DOMAIN.x0),
                y0: readF64(this.m, p + DOMAIN.y0),
                x1: readF64(this.m, p + DOMAIN.x1),
                y1: readF64(this.m, p + DOMAIN.y1),
            };
        });
    }
    /** Solver discretization for a mesh overlay; null for BEM (no mesh). */
    mesh() {
        const mPtr = withScratch(this.m, 4, (outPtr) => {
            const r = this.m.ccall("xsec_solution_mesh", "number", ["number", "number"], [this.s, outPtr]);
            return r >= 0 ? readPtr(this.m, outPtr) : 0;
        });
        if (!mPtr)
            return null;
        try {
            const m = this.m;
            const kind = readI32(m, mPtr + FIELD_MESH.kind);
            const nNodes = readI32(m, mPtr + FIELD_MESH.nNodes);
            const nTris = readI32(m, mPtr + FIELD_MESH.nTris);
            const triEpsPtr = readPtr(m, mPtr + FIELD_MESH.triEpsilon);
            if (kind === 0) {
                return {
                    kind: "triangles",
                    xy: copyF64(m, readPtr(m, mPtr + FIELD_MESH.xy), 2 * nNodes),
                    triangles: copyI32(m, readPtr(m, mPtr + FIELD_MESH.tris), 3 * nTris),
                    triEpsilon: triEpsPtr ? copyF64(m, triEpsPtr, nTris) : null,
                    gx: null,
                    gy: null,
                };
            }
            const nx = readI32(m, mPtr + FIELD_MESH.nx);
            const ny = readI32(m, mPtr + FIELD_MESH.ny);
            return {
                kind: "grid",
                xy: new Float64Array(0),
                triangles: null,
                triEpsilon: null,
                gx: copyF64(m, readPtr(m, mPtr + FIELD_MESH.gx), nx),
                gy: copyF64(m, readPtr(m, mPtr + FIELD_MESH.gy), ny),
            };
        }
        finally {
            this.m.ccall("xsec_field_mesh_destroy", null, ["number"], [mPtr]);
        }
    }
    /** BEM conductor-panel surface charge (the cheap boundary plot); null for mesh backings. */
    surfaceCharge() {
        const sc = withScratch(this.m, 4, (outPtr) => {
            const r = this.m.ccall("xsec_solution_surface_charge", "number", ["number", "number"], [this.s, outPtr]);
            return r >= 0 ? readPtr(this.m, outPtr) : 0;
        });
        if (!sc)
            return null;
        try {
            const m = this.m;
            const n = readI32(m, sc + SURFACE_CHARGE.n);
            return {
                x: copyF64(m, readPtr(m, sc + SURFACE_CHARGE.x), n),
                y: copyF64(m, readPtr(m, sc + SURFACE_CHARGE.y), n),
                sigma: copyF64(m, readPtr(m, sc + SURFACE_CHARGE.sigma), n),
            };
        }
        finally {
            this.m.ccall("xsec_surface_charge_destroy", null, ["number"], [sc]);
        }
    }
    /** Per-level adaptive refinement trajectory (empty for a one-shot solve). */
    history() {
        const n = withScratch(this.m, 4, (p) => {
            check(this.m, this.m.ccall("xsec_solution_history_count", "number", ["number", "number"], [this.s, p]));
            return readI32(this.m, p);
        });
        const levels = [];
        withScratch(this.m, SOLVE_LEVEL.size, (lv) => {
            for (let i = 0; i < n; i++) {
                check(this.m, this.m.ccall("xsec_solution_history_level", "number", ["number", "number", "number"], [this.s, i, lv]));
                levels.push({
                    step: readI32(this.m, lv + SOLVE_LEVEL.step),
                    dof: readI32(this.m, lv + SOLVE_LEVEL.dof),
                    elements: readI32(this.m, lv + SOLVE_LEVEL.elements),
                    z0: readF64(this.m, lv + SOLVE_LEVEL.z0),
                    cap: readF64(this.m, lv + SOLVE_LEVEL.cap),
                    epsEff: readF64(this.m, lv + SOLVE_LEVEL.epsEff),
                    errorEstimate: readF64(this.m, lv + SOLVE_LEVEL.errorEstimate),
                    tMs: readF64(this.m, lv + SOLVE_LEVEL.tMs),
                    energyError: readF64(this.m, lv + SOLVE_LEVEL.energyError),
                });
            }
        });
        return levels;
    }
    /** Waveport eigenmodes (TE/TM/HYBRID analyses), ascending cutoff. */
    modes() {
        const n = withScratch(this.m, 4, (p) => {
            const r = this.m.ccall("xsec_solution_mode_count", "number", ["number", "number"], [this.s, p]);
            return r >= 0 ? readI32(this.m, p) : 0;
        });
        const out = [];
        withScratch(this.m, MODE.size, (mp) => {
            for (let i = 0; i < n; i++) {
                check(this.m, this.m.ccall("xsec_solution_mode", "number", ["number", "number", "number"], [this.s, i, mp]));
                out.push({
                    fc: readF64(this.m, mp + MODE.fc),
                    kc: readF64(this.m, mp + MODE.kc),
                    gamma: readF64(this.m, mp + MODE.gamma),
                    zMode: readF64(this.m, mp + MODE.zMode),
                    zPi: readF64(this.m, mp + MODE.zPi),
                    type: readI32(this.m, mp + MODE.type),
                });
            }
        });
        return out;
    }
    /** Differential-pair results; null for a plain quasi-TEM solve. */
    diff() {
        return withScratch(this.m, DIFF.size, (p) => {
            const r = this.m.ccall("xsec_solution_diff", "number", ["number", "number"], [this.s, p]);
            if (r < 0)
                return null;
            return {
                zOdd: readF64(this.m, p + DIFF.zOdd),
                zEven: readF64(this.m, p + DIFF.zEven),
                zDiff: readF64(this.m, p + DIFF.zDiff),
                zCommon: readF64(this.m, p + DIFF.zCommon),
                epsEffOdd: readF64(this.m, p + DIFF.epsEffOdd),
                epsEffEven: readF64(this.m, p + DIFF.epsEffEven),
                cOdd: readF64(this.m, p + DIFF.cOdd),
                cEven: readF64(this.m, p + DIFF.cEven),
            };
        });
    }
    /** Free the native solution (idempotent). */
    dispose() {
        if (!this.disposed) {
            this.m.ccall("xsec_solution_destroy", null, ["number"], [this.s]);
            this.disposed = true;
        }
    }
}
